import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CHAT_PAGE_SIZE,
  MAX_MESSAGE_CHARS,
  canEditMessage,
  conversationLabel,
  renderMentions,
  type ChatMessageView,
  type ConversationView,
} from '@goyone/shared';
import { api } from '../lib/api';
import { dateTime, longDay, num, time } from '../lib/format';
import { useAuth, useSession } from '../auth/AuthProvider';
import { Icon } from './Icon';
import { Logo } from './Logo';
import { DockHome, JoyJobsView, JoyView } from './JoyPanel';
import { Avatar, useTeamDirectory, type TeamMember } from './People';
import { useToast } from './Toast';
import {
  MessageMenu,
  ForwardPicker,
  copyText,
  buildReplyQuote,
  type MsgAction,
} from './MessageMenu';

/**
 * فقاعةُ الشات العائمة.
 *
 * زرٌّ في زاوية كلّ شاشة يفتح محادثات الفريق فوقها: من يسأل زميلًا وهو
 * في فاتورةٍ لا يغادرها ليكتب ثم يعود يبحث عن موضعه.
 *
 * ## والبيانات هي بيانات صفحة الشات نفسها
 *
 * المفاتيحُ نفسها (`['chat', …]`) والمساراتُ نفسها: رسالةٌ تُكتب في الفقاعة
 * تظهر في الصفحة الكاملة وعدّادُها واحد، لا نسختان تتسابقان.
 *
 * ## بالسحب لا بالدفع — كالصفحة وللسبب نفسه
 *
 * المحادثةُ المفتوحة تُسأل كلّ ثلاث ثوانٍ، والقائمةُ كلّ عشرٍ والفقاعةُ
 * مفتوحة وكلّ عشرين وهي مغلقة. و`react-query` يوقف السحب حين يغيب التبويب.
 */

const OPEN_MS = 3000;
const LIST_OPEN_MS = 10_000;
const LIST_CLOSED_MS = 20_000;

/*
 * لكلّ مستخدمٍ حالتُه: من خرج وترك محادثته مفتوحةً لا يجدها زميلٌ يدخل
 * بعده على الجهاز نفسه مفتوحةً على شاشته.
 */
const stateKey = (userId: string) => `goyone:chat-dock:${userId}`;

type View =
  | { name: 'home' }
  | { name: 'joy' }
  | { name: 'joy-jobs' }
  | { name: 'list' }
  | { name: 'people' }
  | { name: 'group' }
  | { name: 'room'; id: string }
  | { name: 'info'; id: string }
  | { name: 'add'; id: string };

interface DockState {
  open: boolean;
  view: View;
}

/* الحالةُ تبقى بين إعادات التحميل في الجلسة نفسها — تخزينٌ محظورٌ يعني بدايةً مغلقة. */
function readState(userId: string): DockState {
  try {
    const raw = sessionStorage.getItem(stateKey(userId));
    if (raw) return JSON.parse(raw) as DockState;
  } catch {
    /* تخزينٌ محظور أو تالف. */
  }
  return { open: false, view: { name: 'list' } };
}

function writeState(userId: string, state: DockState): void {
  try {
    sessionStorage.setItem(stateKey(userId), JSON.stringify(state));
  } catch {
    /* تعمل الفقاعة ولا تُحفظ حالتُها. */
  }
}

export function ChatDock() {
  const location = useLocation();
  const session = useSession();
  const { atLeast } = useAuth();
  /* جوي لمدير النظام: أدواتُه تقرأ الشركةَ كلَّها، والخادمُ يرفض من دونه. */
  const canJoy = atLeast('ADMIN');
  const [state, setState] = useState<DockState>(() => readState(session.user.id));
  const lastTotal = useRef<number | null>(null);

  const update = (next: DockState) => {
    setState(next);
    writeState(session.user.id, next);
  };
  const go = (view: View) => update({ open: true, view });

  /*
   * يُفتح من زرَّي الشريطِ السفليّ — أيقونةٌ للفريقِ وأخرى لجوي — عبرَ حدثٍ
   * عامٍّ يحمل وجهتَه، فلا فقاعةٌ عائمةٌ تطفو فوقَ المحتوى.
   *
   * `detail.view`: «team» يفتح محادثاتِ الفريقِ مباشرةً، و«joy» يفتح جوي،
   * وبلا وجهةٍ تُفتح شاشةُ الاختيارِ (للمديرِ) أو آخرُ ما كان.
   */
  useEffect(() => {
    const open = (e: Event) => {
      const dest = (e as CustomEvent<{ view?: 'team' | 'joy' }>).detail?.view;
      const view: View =
        dest === 'joy' && canJoy
          ? { name: 'joy' }
          : dest === 'team'
            ? { name: 'list' }
            : canJoy
              ? { name: 'home' }
              : state.view;
      const next: DockState = { open: true, view };
      setState(next);
      writeState(session.user.id, next);
    };
    window.addEventListener('goyone:chat-open', open);
    return () => window.removeEventListener('goyone:chat-open', open);
  }, [canJoy, state.view, session.user.id]);

  const list = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => api<ConversationView[]>('/chat/conversations'),
    refetchInterval: state.open ? LIST_OPEN_MS : LIST_CLOSED_MS,
  });

  const total = (list.data ?? []).reduce((sum, c) => sum + c.unread, 0);

  /*
   * الفقاعةُ تهتزّ حين يزيد غيرُ المقروء — لا حين يُفتح النظام.
   *
   * هزّةٌ عند كلّ تحميل تُعلّم العين تجاهلها، فلا تلتفت يوم تصل رسالةٌ فعلًا.
   */
  /*
   * رابطُ إشعارٍ من جوي (`#joy`) يفتح محادثته فوق الشاشة الحالية.
   * والعلامةُ تُزال بعد الفتح: رجوعٌ في المتصفّح لا يفتحها ثانيةً.
   */
  useEffect(() => {
    if (location.hash !== '#joy' || !canJoy) return;
    update({ open: true, view: { name: 'joy' } });
    window.history.replaceState(null, '', location.pathname + location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, canJoy]);

  const loaded = list.data !== undefined;
  useEffect(() => {
    if (!loaded) return undefined;
    const prev = lastTotal.current;
    lastTotal.current = total;
    if (prev === null || total <= prev) return undefined;
    /* رسالةٌ جديدة — ينبضُ زرُّ الشاتِ في الشريطِ السفليّ. */
    window.dispatchEvent(new Event('goyone:chat-bump'));
    return undefined;
  }, [total, loaded]);

  /* الصفحةُ الكاملة مفتوحة — فقاعةٌ فوقها تكرارٌ لما تحتها. */
  if (location.pathname === '/chat' || location.pathname.startsWith('/chat/')) return null;

  const conversations = list.data ?? [];
  /* من لم يعد مديرًا وبقيت حالتُه على جوي يعود إلى محادثات الفريق. */
  const joyView = ['home', 'joy', 'joy-jobs'].includes(state.view.name);
  const view: View = joyView && !canJoy ? { name: 'list' } : state.view;
  const close = () => update({ ...state, open: false });
  const current =
    view.name === 'room' || view.name === 'info' || view.name === 'add'
      ? (conversations.find((c) => c.id === (view as { id: string }).id) ?? null)
      : null;

  /*
   * محادثةٌ فُتحت ثم خرج منها صاحبُها أو أُخرج — تعود القائمة.
   * والانتظارُ حتى تصل القائمةُ أوّلًا: قبلها كلُّ محادثةٍ «غير موجودة».
   */
  const lost =
    list.data !== undefined &&
    (view.name === 'room' || view.name === 'info' || view.name === 'add') &&
    !current;

  return (
    <div className={`dock${state.open ? ' is-open' : ''}`}>
      {state.open && (
        <section className="dock-panel" role="dialog" aria-label="المحادثات">
          {view.name === 'home' && (
            <DockHome
              teamUnread={total}
              onJoy={() => go({ name: 'joy' })}
              onTeam={() => go({ name: 'list' })}
              onClose={close}
            />
          )}

          {view.name === 'joy' && (
            <JoyView
              onBack={() => go({ name: 'home' })}
              onJobs={() => go({ name: 'joy-jobs' })}
              onClose={close}
            />
          )}

          {view.name === 'joy-jobs' && (
            <JoyJobsView onBack={() => go({ name: 'joy' })} onClose={close} />
          )}

          {(view.name === 'list' || lost) && (
            <ListView
              onBack={canJoy ? () => go({ name: 'home' }) : undefined}
              conversations={conversations}
              loading={list.isPending}
              error={list.isError}
              onOpen={(id) => go({ name: 'room', id })}
              onNew={() => go({ name: 'people' })}
              onGroup={() => go({ name: 'group' })}
              onClose={() => update({ ...state, open: false })}
            />
          )}

          {view.name === 'people' && (
            <PeopleView
              meId={session.user.id}
              onBack={() => go({ name: 'list' })}
              onGroup={() => go({ name: 'group' })}
              onOpened={(id) => go({ name: 'room', id })}
            />
          )}

          {view.name === 'group' && (
            <GroupView
              meId={session.user.id}
              onBack={() => go({ name: 'people' })}
              onCreated={(id) => go({ name: 'room', id })}
            />
          )}

          {view.name === 'room' && current && (
            <RoomView
              key={current.id}
              conversation={current}
              onBack={() => go({ name: 'list' })}
              onInfo={() => go({ name: 'info', id: current.id })}
              onClose={() => update({ ...state, open: false })}
            />
          )}

          {view.name === 'info' && current && (
            <InfoView
              conversation={current}
              onBack={() => go({ name: 'room', id: current.id })}
              onAdd={() => go({ name: 'add', id: current.id })}
              onLeft={() => go({ name: 'list' })}
            />
          )}

          {view.name === 'add' && current && (
            <AddView
              conversation={current}
              meId={session.user.id}
              onBack={() => go({ name: 'info', id: current.id })}
            />
          )}
        </section>
      )}

      {/*
        الزرُّ العائم للفتح وحدَه.
        اللوحةُ المفتوحة تنزل إلى أسفل الشاشة كنافذة شاتٍ عاديّة وتُغلق من
        رأسها — وزرٌّ ثانٍ للإغلاق تحتها يأخذ مكانها ولا يزيد شيئًا.
      */}
      {/* لا فقاعةٌ عائمة — يُفتح الشاتُ من زرِّه في الشريطِ السفليّ (مساحةِ الأمان). */}
    </div>
  );
}

/* ==========================================================================
   العلامة
   ========================================================================== */

/**
 * علامةُ الفقاعة: حرفُ GoyOne داخل فقاعة كلام.
 *
 * لا أيقونةَ واتساب ولا فقاعةً عامّة: الزرُّ يقول «هذا حديثُ شركتك في
 * نظامها» — وأيقونةٌ مستعارةٌ توحي أنّ الرسالة تخرج إلى تطبيقٍ آخر.
 */
function DockMark() {
  return (
    <span className="dock-mark" aria-hidden="true">
      <svg viewBox="0 0 64 64" className="dock-bubble">
        <path d="M32 5C17.1 5 5 16.4 5 30.5c0 6.3 2.4 12 6.4 16.5L8 59l13.1-5.6c3.4 1.4 7.1 2.1 10.9 2.1 14.9 0 27-11.4 27-25S46.9 5 32 5Z" />
      </svg>
      <span className="dock-logo">
        <Logo size={26} mono />
      </span>
    </span>
  );
}

/* ==========================================================================
   القائمة
   ========================================================================== */

function ListView({
  onBack,
  conversations,
  loading,
  error,
  onOpen,
  onNew,
  onGroup,
  onClose,
}: {
  onBack?: () => void;
  conversations: ConversationView[];
  loading: boolean;
  error: boolean;
  onOpen: (id: string) => void;
  onNew: () => void;
  onGroup: () => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const { byId } = useTeamDirectory();

  /*
   * ما أنا عضوٌ فيه وحدَه.
   *
   * القناةُ العامّة التي لم أنضمّ إليها تُقرأ في الصفحة الكاملة، وعرضُها
   * هنا يملأ الفقاعة بقنواتٍ لا أتابعها فتضيع بينها محادثاتي.
   */
  const mine = conversations
    .filter((c) => c.joined)
    .filter((c) => !q.trim() || c.title.includes(q.trim()));

  return (
    <>
      <header className="dock-head">
        {onBack ? (
          <BackButton onClick={onBack} />
        ) : (
          <span className="dock-head-brand">
            <Logo size={22} mono />
          </span>
        )}
        <div className="dock-head-text">
          <strong>محادثات الفريق</strong>
          <span className="tiny">داخل GoyOne — لا تخرج من الشركة</span>
        </div>
        <button
          className="dock-icon-btn"
          onClick={onGroup}
          title="مجموعة جديدة"
          aria-label="مجموعة جديدة"
        >
          <Icon name="users" size={17} />
        </button>
        <Link className="dock-icon-btn" to="/chat" title="بملء الصفحة" aria-label="بملء الصفحة">
          <Icon name="expand" size={16} />
        </Link>
        <button className="dock-icon-btn" onClick={onClose} title="إغلاق" aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="dock-search">
        <Icon name="search" size={15} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث في محادثاتك"
          aria-label="ابحث في محادثاتك"
        />
      </div>

      <div className="dock-body">
        {loading && <div className="skeleton" style={{ height: 64, margin: 12 }} />}
        {error && <p className="dock-empty">تعذّر جلب المحادثات. تُعاد المحاولة تلقائيًّا.</p>}

        {!loading && !error && mine.length === 0 && (
          <div className="dock-empty">
            <p>{q.trim() ? 'لا محادثة بهذا الاسم.' : 'لا محادثات بعد.'}</p>
            {!q.trim() && (
              <button className="btn btn-primary btn-sm" onClick={onNew}>
                ابدأ محادثة مع زميل
              </button>
            )}
          </div>
        )}

        <ul className="dock-list">
          {mine.map((c) => (
            <li key={c.id}>
              <button className="dock-row" onClick={() => onOpen(c.id)}>
                <ConversationAvatar
                  conversation={c}
                  member={c.peerId ? byId.get(c.peerId) : undefined}
                />
                <span className="dock-row-main">
                  <span className="dock-row-top">
                    <strong className="dock-row-title">{c.title}</strong>
                    {c.lastAt && (
                      <span className={`tiny${c.unread ? ' dock-fresh' : ' mute'}`}>
                        {when(c.lastAt)}
                      </span>
                    )}
                  </span>
                  <span className="dock-row-bottom">
                    <span className="tiny mute dock-row-last">
                      {/* المقتطفُ يُحفظ خامًا: الذكرُ رمزٌ يُترجم اسمًا هنا كما في الرسالة. */}
                      {c.lastBody
                        ? `${c.kind !== 'DIRECT' && c.lastAuthorName ? `${c.lastAuthorName.split(' ')[0]}: ` : ''}${renderMentions(c.lastBody, (id) => byId.get(id)?.name)}`
                        : conversationLabel(c)}
                    </span>
                    {c.unread > 0 && <span className="dock-unread num">{num(c.unread)}</span>}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <button className="dock-fab" onClick={onNew} title="محادثة جديدة" aria-label="محادثة جديدة">
        <Icon name="plus" size={20} />
      </button>
    </>
  );
}

function ConversationAvatar({
  conversation,
  member,
  size = 40,
}: {
  conversation: ConversationView;
  member?: TeamMember;
  size?: number;
}) {
  if (conversation.kind === 'DIRECT') {
    return <Avatar name={conversation.title} src={member?.avatarUrl} size={size} />;
  }
  return (
    <span className="dock-group-avatar" style={{ width: size, height: size }} aria-hidden="true">
      {conversation.isPrivate ? <Icon name="users" size={size * 0.45} /> : '#'}
    </span>
  );
}

/** كم يومًا تقويميًّا مضى — لا كم أربعًا وعشرين ساعة: رسالةُ منتصف الليل «أمس». */
function daysAgo(iso: string): number {
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((startOf(new Date()) - startOf(new Date(iso))) / 86_400_000);
}

/** «١٠:٣٠» اليوم، و«أمس»، وتاريخٌ قصير لما قبلهما — كما في كلّ شات. */
function when(iso: string): string {
  const days = daysAgo(iso);
  if (days <= 0) return time(iso);
  if (days === 1) return 'أمس';
  return new Date(iso).toLocaleDateString('ar-EG', { day: 'numeric', month: 'short' });
}

/* ==========================================================================
   اختيار زميل
   ========================================================================== */

function PeopleView({
  meId,
  onBack,
  onGroup,
  onOpened,
}: {
  meId: string;
  onBack: () => void;
  onGroup: () => void;
  onOpened: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { assignable, isPending } = useTeamDirectory();
  const [q, setQ] = useState('');

  const open = useMutation({
    mutationFn: (userId: string) =>
      api<ConversationView>('/chat/direct', { method: 'POST', body: { userId } }),
    onSuccess: async (conv) => {
      /* القائمةُ أوّلًا: الغرفة تُفتح على محادثةٍ تعرفها القائمة. */
      await queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      onOpened(conv.id);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر فتح المحادثة.'),
  });

  const others = filterPeople(
    assignable.filter((m) => m.userId !== meId),
    q,
  );

  return (
    <>
      <header className="dock-head">
        <BackButton onClick={onBack} />
        <div className="dock-head-text">
          <strong>محادثة جديدة</strong>
          <span className="tiny">اختر من تكلّمه</span>
        </div>
      </header>

      <div className="dock-search">
        <Icon name="search" size={15} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث باسم الزميل أو وظيفته"
          aria-label="ابحث باسم الزميل"
          autoFocus
        />
      </div>

      <div className="dock-body">
        <button className="dock-row dock-row-action" onClick={onGroup}>
          <span className="dock-group-avatar is-brand" style={{ width: 40, height: 40 }}>
            <Icon name="users" size={18} />
          </span>
          <span className="dock-row-main">
            <strong>مجموعة جديدة</strong>
            <span className="tiny mute">عدّة زملاء في محادثةٍ واحدة</span>
          </span>
        </button>

        <p className="dock-section">الزملاء</p>

        {isPending && <div className="skeleton" style={{ height: 64, margin: 12 }} />}
        {!isPending && others.length === 0 && (
          <p className="dock-empty">
            {q.trim() ? 'لا زميل بهذا الاسم.' : 'لا زملاء في الشركة بعد.'}
          </p>
        )}

        <ul className="dock-list">
          {others.map((m) => (
            <li key={m.userId}>
              <button
                className="dock-row"
                onClick={() => open.mutate(m.userId)}
                disabled={open.isPending}
              >
                <Avatar name={m.name} src={m.avatarUrl} size={40} />
                <span className="dock-row-main">
                  <strong className="dock-row-title">{m.name}</strong>
                  <span className="tiny mute">{m.jobTitle ?? m.email}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

function filterPeople(people: TeamMember[], q: string): TeamMember[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return people;
  return people.filter(
    (m) =>
      m.name.toLowerCase().includes(needle) ||
      (m.jobTitle ?? '').toLowerCase().includes(needle) ||
      m.email.toLowerCase().includes(needle),
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="dock-icon-btn" onClick={onClick} title="رجوع" aria-label="رجوع">
      <Icon name="chevronRight" size={20} />
    </button>
  );
}

/* ==========================================================================
   مجموعة جديدة
   ========================================================================== */

function GroupView({
  meId,
  onBack,
  onCreated,
}: {
  meId: string;
  onBack: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { assignable, byId } = useTeamDirectory();
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api<ConversationView>('/chat/conversations', {
        method: 'POST',
        /* المجموعةُ خاصّةٌ دائمًا: يراها من فيها وحدَهم. */
        body: { name: name.trim(), isPrivate: true, memberIds: picked },
      }),
    onSuccess: async (conv) => {
      await queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      onCreated(conv.id);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر إنشاء المجموعة.'),
  });

  const people = filterPeople(
    assignable.filter((m) => m.userId !== meId),
    q,
  );
  const ready = name.trim().length >= 2 && picked.length > 0;

  return (
    <>
      <header className="dock-head">
        <BackButton onClick={onBack} />
        <div className="dock-head-text">
          <strong>مجموعة جديدة</strong>
          <span className="tiny">
            {picked.length ? `${num(picked.length)} مختارون` : 'سمِّها واختر أعضاءها'}
          </span>
        </div>
      </header>

      <div className="dock-form">
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 80))}
          placeholder="اسم المجموعة — مثلًا: فريق عرض شركة النور"
          aria-label="اسم المجموعة"
          autoFocus
        />

        {picked.length > 0 && (
          <div className="dock-chips">
            {picked.map((id) => (
              <button
                key={id}
                className="dock-chip"
                onClick={() => setPicked((cur) => cur.filter((x) => x !== id))}
                title="إزالة"
              >
                {byId.get(id)?.name ?? 'زميل'}
                <Icon name="x" size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="dock-search">
        <Icon name="search" size={15} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث عن زميل"
          aria-label="ابحث عن زميل"
        />
      </div>

      <div className="dock-body">
        <PickList
          people={people}
          picked={picked}
          onToggle={(id) =>
            setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
          }
        />
      </div>

      <div className="dock-footer">
        <button
          className="btn btn-primary"
          onClick={() => create.mutate()}
          disabled={!ready || create.isPending}
        >
          {create.isPending ? 'جارٍ الإنشاء' : 'أنشئ المجموعة'}
        </button>
      </div>
    </>
  );
}

function PickList({
  people,
  picked,
  onToggle,
}: {
  people: TeamMember[];
  picked: string[];
  onToggle: (id: string) => void;
}) {
  if (!people.length) return <p className="dock-empty">لا زملاء يطابقون.</p>;
  return (
    <ul className="dock-list">
      {people.map((m) => {
        const on = picked.includes(m.userId);
        return (
          <li key={m.userId}>
            <label className={`dock-row${on ? ' is-picked' : ''}`}>
              <input type="checkbox" checked={on} onChange={() => onToggle(m.userId)} />
              <Avatar name={m.name} src={m.avatarUrl} size={36} />
              <span className="dock-row-main">
                <strong className="dock-row-title">{m.name}</strong>
                <span className="tiny mute">{m.jobTitle ?? m.email}</span>
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

/* ==========================================================================
   المحادثة
   ========================================================================== */

function RoomView({
  conversation,
  onBack,
  onInfo,
  onClose,
}: {
  conversation: ConversationView;
  onBack: () => void;
  onInfo: () => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const { atLeast } = useAuth();
  const { byId } = useTeamDirectory();
  const me = session.user.id;

  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const [older, setOlder] = useState<ChatMessageView[]>([]);
  const [noMore, setNoMore] = useState(false);
  /* الرسالةُ المُحوَّلةُ — تُنتقى لها وجهةٌ ثم تُرسَل نصُّها إليها. */
  const [forwarding, setForwarding] = useState<ChatMessageView | null>(null);
  const stream = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const pinned = useRef(true);

  /* قائمةُ المحادثاتِ لوجهةِ التحويل — من ذاكرةِ الاستعلامِ نفسِها. */
  const convos = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => api<ConversationView[]>('/chat/conversations'),
  });

  const latest = useQuery({
    queryKey: ['chat', 'messages', conversation.id],
    queryFn: () => api<ChatMessageView[]>(`/chat/conversations/${conversation.id}/messages`),
    refetchInterval: OPEN_MS,
  });

  /*
   * الأقدمُ المجلوبُ يدويًّا يُضمّ إلى الأحدث المسحوب.
   *
   * والتكرارُ يُحذف بالمعرّف: رسالةٌ في آخر صفحةٍ قديمة قد تكون في أوّل
   * صفحة السحب إن قلّت الرسائل بين الجلبين.
   */
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: ChatMessageView[] = [];
    for (const m of [...older, ...(latest.data ?? [])]) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
    return out;
  }, [older, latest.data]);

  const lastId = rows.length ? rows[rows.length - 1]!.id : null;
  const firstPageFull = (latest.data?.length ?? 0) >= CHAT_PAGE_SIZE;

  /* القراءةُ تُعلَّم عند الفتح وعند وصول جديد — لا مع كلّ نبضة سحب. */
  useEffect(() => {
    if (!lastId) return;
    void api(`/chat/conversations/${conversation.id}/read`, { method: 'POST' })
      .then(() => queryClient.invalidateQueries({ queryKey: ['chat', 'conversations'] }))
      .catch(() => undefined);
  }, [conversation.id, lastId, queryClient]);

  /*
   * النزولُ إلى الجديد لمن هو في الأسفل وحدَه.
   *
   * من صعد يقرأ ما فاته لا يُسحب إلى الأسفل كلّ ثلاث ثوانٍ مع كلّ رسالة.
   */
  useEffect(() => {
    const el = stream.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  useEffect(() => {
    input.current?.focus();
  }, []);

  const loadOlder = useMutation({
    mutationFn: () =>
      api<ChatMessageView[]>(
        `/chat/conversations/${conversation.id}/messages?before=${rows[0]!.id}`,
      ),
    onSuccess: (page) => {
      const el = stream.current;
      const from = el ? el.scrollHeight - el.scrollTop : 0;
      if (page.length < CHAT_PAGE_SIZE) setNoMore(true);
      setOlder((cur) => [...page, ...cur]);
      /* الموضعُ يبقى تحت العين: ما يُضاف فوقُ لا يدفع ما يُقرأ. */
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - from;
      });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر جلب الأقدم.'),
  });

  const send = useMutation({
    mutationFn: (body: string) =>
      api<ChatMessageView>(`/chat/conversations/${conversation.id}/messages`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: () => {
      setDraft('');
      pinned.current = true;
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  const edit = useMutation({
    mutationFn: (args: { id: string; body: string }) =>
      api<ChatMessageView>(`/chat/messages/${args.id}`, {
        method: 'PATCH',
        body: { body: args.body },
      }),
    onSuccess: (updated) => {
      setEditing(null);
      setOlder((cur) => cur.map((m) => (m.id === updated.id ? updated : m)));
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التعديل.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/chat/messages/${id}`, { method: 'DELETE' }),
    onSuccess: (_r, id) => {
      setOlder((cur) =>
        cur.map((m) => (m.id === id ? { ...m, body: '', deletedAt: new Date().toISOString() } : m)),
      );
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  /* التحويلُ إرسالٌ إلى محادثةٍ أخرى بنصِّ الرسالةِ نفسِه. */
  const forward = useMutation({
    mutationFn: (args: { toId: string; body: string }) =>
      api(`/chat/conversations/${args.toId}/messages`, {
        method: 'POST',
        body: { body: args.body },
      }),
    onSuccess: () => {
      setForwarding(null);
      toast('حُوّلت الرسالة');
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التحويل.'),
  });

  /* أفعالُ الرسالةِ في قائمتها — ردٌّ ونسخٌ وتحويلٌ للجميع، وتعديلٌ وحذفٌ بحسبِ الحقّ. */
  const buildActions = (m: ChatMessageView): MsgAction[] => {
    const mine = m.authorId === me;
    const acts: MsgAction[] = [
      {
        key: 'reply',
        label: 'ردّ',
        icon: 'reply',
        onClick: () => {
          setDraft(buildReplyQuote(m.authorName, m.body));
          input.current?.focus();
        },
      },
      {
        key: 'copy',
        label: 'نسخ',
        icon: 'copy',
        onClick: () => void copyText(m.body).then((ok) => toast(ok ? 'نُسخت الرسالة' : 'تعذّر النسخ')),
      },
      { key: 'forward', label: 'تحويل', icon: 'forward', onClick: () => setForwarding(m) },
    ];
    if (mine && canEditMessage(m.createdAt)) {
      acts.push({
        key: 'edit',
        label: 'تعديل',
        icon: 'edit',
        onClick: () => setEditing({ id: m.id, body: m.body }),
      });
    }
    if (mine || (atLeast('ADMIN') && isGroup)) {
      acts.push({
        key: 'delete',
        label: 'حذف',
        icon: 'trash',
        danger: true,
        onClick: () => remove.mutate(m.id),
      });
    }
    return acts;
  };

  const submit = () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  };

  const peer = conversation.peerId ? byId.get(conversation.peerId) : undefined;
  const isGroup = conversation.kind === 'CHANNEL';
  const nameOf = (userId: string) => byId.get(userId)?.name;

  return (
    <>
      <header className="dock-head">
        <BackButton onClick={onBack} />
        <button
          className="dock-head-who"
          onClick={isGroup ? onInfo : undefined}
          disabled={!isGroup}
          title={isGroup ? 'معلومات المجموعة' : undefined}
        >
          <ConversationAvatar conversation={conversation} member={peer} size={34} />
          <span className="dock-head-text">
            <strong>{conversation.title}</strong>
            <span className="tiny">
              {isGroup
                ? `${conversationLabel(conversation)} · ${num(conversation.memberCount)} عضوًا`
                : (peer?.jobTitle ?? 'محادثة مباشرة — بينكما وحدكما')}
            </span>
          </span>
        </button>
        <Link
          className="dock-icon-btn"
          to={`/chat/${conversation.id}`}
          title="بملء الصفحة"
          aria-label="بملء الصفحة"
        >
          <Icon name="expand" size={16} />
        </Link>
        <button className="dock-icon-btn" onClick={onClose} title="إغلاق" aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div
        className="dock-stream"
        ref={stream}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        }}
      >
        {latest.isPending && <div className="skeleton" style={{ height: 80 }} />}
        {latest.isError && (
          <p className="dock-empty">تعذّر جلب الرسائل. تُعاد المحاولة تلقائيًّا.</p>
        )}

        {firstPageFull && !noMore && rows.length > 0 && (
          <button
            className="dock-older"
            onClick={() => loadOlder.mutate()}
            disabled={loadOlder.isPending}
          >
            {loadOlder.isPending ? 'جارٍ الجلب' : 'رسائل أقدم'}
          </button>
        )}

        {latest.data && rows.length === 0 && (
          <p className="dock-hint">
            {isGroup
              ? 'لا رسائل بعد. ابدأ الحديث.'
              : `لا رسائل بعد. اكتب إلى ${conversation.title}.`}
          </p>
        )}

        {rows.map((m, i) => {
          const prev = rows[i - 1];
          const newDay = !prev || dayKey(prev.createdAt) !== dayKey(m.createdAt);
          const mine = m.authorId === me && !m.system;
          /* رسائلُ متتابعةٌ من شخصٍ واحد تُجمع: اسمٌ فوق كلّ سطرٍ يُغرق الشاشة. */
          const grouped =
            !newDay && prev !== undefined && !prev.system && prev.authorId === m.authorId;

          return (
            <div key={m.id} className="dock-msg-wrap">
              {newDay && <div className="dock-day">{dayLabel(m.createdAt)}</div>}

              {m.system ? (
                <div className="dock-event" title={dateTime(m.createdAt)}>
                  {m.body}
                  {m.authorName && <span className="dock-event-by"> — {m.authorName}</span>}
                </div>
              ) : (
                <div
                  className={`dock-msg ${mine ? 'is-out' : 'is-in'}${grouped ? ' is-grouped' : ''}`}
                >
                  {isGroup && !mine && !grouped && (
                    <span
                      className="dock-author"
                      style={{ color: `hsl(${hue(m.authorName ?? '')} 55% 42%)` }}
                    >
                      {m.authorName ?? 'مستخدم سابق'}
                    </span>
                  )}

                  {m.deletedAt ? (
                    <span className="dock-deleted">حُذفت هذه الرسالة</span>
                  ) : editing?.id === m.id ? (
                    <div className="dock-edit">
                      <textarea
                        className="textarea"
                        rows={2}
                        value={editing.body}
                        onChange={(e) =>
                          setEditing({ id: m.id, body: e.target.value.slice(0, MAX_MESSAGE_CHARS) })
                        }
                        autoFocus
                      />
                      <span className="row" style={{ gap: 6 }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => edit.mutate({ id: m.id, body: editing.body })}
                          disabled={!editing.body.trim() || edit.isPending}
                        >
                          حفظ
                        </button>
                        <button className="btn btn-sm" onClick={() => setEditing(null)}>
                          إلغاء
                        </button>
                      </span>
                    </div>
                  ) : (
                    <span className="dock-text">{renderMentions(m.body, nameOf)}</span>
                  )}

                  <span className="dock-meta" title={dateTime(m.createdAt)}>
                    {m.editedAt && !m.deletedAt && 'عُدّلت · '}
                    {time(m.createdAt)}
                  </span>

                  {!m.deletedAt && editing?.id !== m.id && (
                    <MessageMenu align={mine ? 'start' : 'end'} actions={buildActions(m)} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="dock-composer">
        <textarea
          ref={input}
          className="dock-input"
          rows={1}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value.slice(0, MAX_MESSAGE_CHARS));
            /* الحقلُ يطول مع النصّ حتى خمسة أسطر، ثم يُمرَّر داخله. */
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
          }}
          onKeyDown={(e) => {
            /* Enter يُرسل، وShift+Enter يُنزل سطرًا — كما في كلّ شات. */
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="اكتب رسالة"
          aria-label={`رسالة إلى ${conversation.title}`}
        />
        <button
          className="dock-send"
          onClick={submit}
          disabled={send.isPending || !draft.trim()}
          title="إرسال"
          aria-label="إرسال"
        >
          <Icon name="send" size={18} />
        </button>
      </div>

      {forwarding && (
        <ForwardPicker
          conversations={convos.data ?? []}
          busy={forward.isPending}
          onClose={() => setForwarding(null)}
          onPick={(toId) => forward.mutate({ toId, body: forwarding.body })}
        />
      )}
    </>
  );
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(iso: string): string {
  const days = daysAgo(iso);
  if (days <= 0) return 'اليوم';
  if (days === 1) return 'أمس';
  return longDay(iso);
}

/** درجةُ لونٍ ثابتةٌ للاسم — الشخصُ نفسه باللون نفسه في كلّ مجموعة. */
function hue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return h;
}

/* ==========================================================================
   معلومات المجموعة
   ========================================================================== */

function InfoView({
  conversation,
  onBack,
  onAdd,
  onLeft,
}: {
  conversation: ConversationView;
  onBack: () => void;
  onAdd: () => void;
  onLeft: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const { atLeast } = useAuth();
  const { byId } = useTeamDirectory();
  const me = session.user.id;
  const [name, setName] = useState(conversation.title);
  /*
   * الإخراجُ والمغادرةُ بنقرتين.
   *
   * من غادر مجموعةً خاصّةً لا يعود إليها إلّا أن يضيفه عضو، ونقرةٌ خاطئةٌ
   * على سطرٍ بجوار اسمٍ تُخرج زميلًا يقرأ الجميعُ «أُخرج» في مجراهم.
   */
  const [confirming, setConfirming] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['chat', 'members', conversation.id],
    queryFn: () =>
      api<Array<{ userId: string; name: string | null }>>(
        `/chat/conversations/${conversation.id}/members`,
      ),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['chat'] });

  const rename = useMutation({
    mutationFn: () =>
      api(`/chat/conversations/${conversation.id}`, {
        method: 'PATCH',
        body: { name: name.trim() },
      }),
    onSuccess: () => {
      toast('تغيّر الاسم.', 'success');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      api(`/chat/conversations/${conversation.id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: (_r, userId) => {
      if (userId === me) {
        refresh();
        onLeft();
        return;
      }
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإخراج.'),
  });

  /* يُخرج غيرَه منشئُ المجموعة ومديرُ الحساب — والخادمُ يرفض غيرهما. */
  const mayRemove = conversation.createdBy === me || atLeast('ADMIN');
  const rows = [...(members.data ?? [])].sort((a, b) =>
    a.userId === conversation.createdBy ? -1 : b.userId === conversation.createdBy ? 1 : 0,
  );

  return (
    <>
      <header className="dock-head">
        <BackButton onClick={onBack} />
        <div className="dock-head-text">
          <strong>معلومات ال{conversationLabel(conversation)}</strong>
          <span className="tiny">{num(conversation.memberCount)} عضوًا</span>
        </div>
      </header>

      <div className="dock-body">
        <div className="dock-info-hero">
          <ConversationAvatar conversation={conversation} size={64} />
          <div className="dock-rename">
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 80))}
              aria-label="اسم المجموعة"
            />
            <button
              className="btn btn-sm"
              onClick={() => rename.mutate()}
              disabled={
                rename.isPending || name.trim().length < 2 || name.trim() === conversation.title
              }
            >
              حفظ الاسم
            </button>
          </div>
          <p className="tiny mute center">
            {conversation.isPrivate
              ? 'مجموعةٌ خاصّة: يقرؤها أعضاؤها وحدهم، وكلُّ عضوٍ يضيف زملاءه.'
              : 'قناةٌ عامّة: يقرؤها كلُّ من في الشركة.'}
          </p>
        </div>

        <button className="dock-row dock-row-action" onClick={onAdd}>
          <span className="dock-group-avatar is-brand" style={{ width: 40, height: 40 }}>
            <Icon name="plus" size={18} />
          </span>
          <span className="dock-row-main">
            <strong>إضافة أعضاء</strong>
          </span>
        </button>

        <p className="dock-section">الأعضاء</p>
        {members.isPending && <div className="skeleton" style={{ height: 64, margin: 12 }} />}

        <ul className="dock-list">
          {rows.map((m) => {
            const person = byId.get(m.userId);
            const display = m.name ?? person?.name ?? 'مستخدم سابق';
            return (
              <li key={m.userId}>
                <div className="dock-row is-static">
                  <Avatar name={display} src={person?.avatarUrl} size={36} />
                  <span className="dock-row-main">
                    <strong className="dock-row-title">
                      {m.userId === me ? `${display} (أنت)` : display}
                    </strong>
                    <span className="tiny mute">
                      {m.userId === conversation.createdBy
                        ? 'منشئ المجموعة'
                        : (person?.jobTitle ?? '')}
                    </span>
                  </span>
                  {mayRemove && m.userId !== me && (
                    <button
                      className="link-btn tiny link-danger"
                      onClick={() => {
                        if (confirming !== m.userId) return setConfirming(m.userId);
                        setConfirming(null);
                        removeMember.mutate(m.userId);
                      }}
                      onBlur={() => setConfirming((c) => (c === m.userId ? null : c))}
                      disabled={removeMember.isPending}
                    >
                      {confirming === m.userId ? 'تأكيد الإخراج' : 'إخراج'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        <button
          className="dock-leave"
          onClick={() => {
            if (confirming !== me) return setConfirming(me);
            setConfirming(null);
            removeMember.mutate(me);
          }}
          onBlur={() => setConfirming((c) => (c === me ? null : c))}
          disabled={removeMember.isPending}
        >
          <Icon name="logout" size={16} />
          {confirming === me
            ? `اضغط ثانيةً لتأكيد المغادرة${conversation.isPrivate ? ' — لا تعود إلّا بإضافة' : ''}`
            : `مغادرة ال${conversationLabel(conversation)}`}
        </button>
      </div>
    </>
  );
}

/* ==========================================================================
   إضافة أعضاء
   ========================================================================== */

function AddView({
  conversation,
  meId,
  onBack,
}: {
  conversation: ConversationView;
  meId: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { assignable } = useTeamDirectory();
  const [picked, setPicked] = useState<string[]>([]);
  const [q, setQ] = useState('');

  const members = useQuery({
    queryKey: ['chat', 'members', conversation.id],
    queryFn: () =>
      api<Array<{ userId: string; name: string | null }>>(
        `/chat/conversations/${conversation.id}/members`,
      ),
  });
  const inside = new Set((members.data ?? []).map((m) => m.userId));

  const add = useMutation({
    mutationFn: () =>
      api<{ added: number }>(`/chat/conversations/${conversation.id}/members`, {
        method: 'POST',
        body: { userIds: picked },
      }),
    onSuccess: (r) => {
      toast(r.added ? `أُضيف ${num(r.added)}.` : 'المختارون أعضاءٌ فيها أصلًا.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['chat'] });
      onBack();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت الإضافة.'),
  });

  const people = filterPeople(
    assignable.filter((m) => m.userId !== meId && !inside.has(m.userId)),
    q,
  );

  return (
    <>
      <header className="dock-head">
        <BackButton onClick={onBack} />
        <div className="dock-head-text">
          <strong>إضافة إلى {conversation.title}</strong>
          <span className="tiny">
            {picked.length ? `${num(picked.length)} مختارون` : 'اختر زملاء'}
          </span>
        </div>
      </header>

      <div className="dock-search">
        <Icon name="search" size={15} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ابحث عن زميل"
          aria-label="ابحث عن زميل"
          autoFocus
        />
      </div>

      <div className="dock-body">
        {members.isPending ? (
          <div className="skeleton" style={{ height: 64, margin: 12 }} />
        ) : (
          <PickList
            people={people}
            picked={picked}
            onToggle={(id) =>
              setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
            }
          />
        )}
      </div>

      <div className="dock-footer">
        <button
          className="btn btn-primary"
          onClick={() => add.mutate()}
          disabled={!picked.length || add.isPending}
        >
          {add.isPending ? 'جارٍ الإضافة' : 'أضف'}
        </button>
      </div>
    </>
  );
}
